import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Icon } from '../components/Icon';
import {
  api,
  ApiError,
  type BridgeState,
  type IntegrationsResponse,
  type NowPlaying,
} from '../lib/api';
import { useElena } from '../state';

const errorText = (err: unknown, fallback: string) =>
  err instanceof ApiError ? err.message : fallback;

/** Reads (and removes) the ?spotify=… result the OAuth callback leaves in the address. */
function takeSpotifyResult(): string | null {
  const params = new URLSearchParams(location.search);
  const result = params.get('spotify');
  if (result === null) return null;
  params.delete('spotify');
  const rest = params.toString();
  history.replaceState(null, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
  return result;
}

export function Integrations() {
  const [data, setData] = useState<IntegrationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(() => {
    const r = takeSpotifyResult();
    if (r === null) return null;
    return r === 'ok'
      ? { ok: true, text: 'Spotify je povezan. Probaj: „pusti Cecu na Spotify-u“.' }
      : { ok: false, text: r };
  });

  const load = useCallback(async () => {
    try {
      setData(await api.integrations());
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Ne mogu da učitam integracije.'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Integracije</h1>
          <p className="muted">
            Gde sve Elena stiže: telefon, Discord, Spotify. Sve je besplatno osim Spotify Premium-a.
          </p>
        </div>
      </header>

      {notice && (
        <p className={`notice${notice.ok ? ' ok' : ''}`} role="status">
          {notice.text}
        </p>
      )}
      {error && <p className="notice">{error}</p>}

      {data && (
        <div className="grid-2 integrations">
          <BridgeCard data={data} />
          <PushCard data={data} reload={load} />
          <DiscordCard data={data} reload={load} onNotice={setNotice} />
          <SpotifyCard data={data} reload={load} />
          <BuiltInCard data={data} />
        </div>
      )}
    </div>
  );
}

interface CardProps {
  data: IntegrationsResponse;
  reload: () => Promise<void>;
}

function PushCard({ data, reload }: CardProps) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const phones = data.push.devices;

  return (
    <section className="card integration" aria-labelledby="int-push">
      <header className="card-head">
        <span className="integration-icon">
          <Icon name="bell" />
        </span>
        <h2 id="int-push" className="grow">
          Notifikacije na telefonu
        </h2>
        <span className={`pill ${phones.length ? 'ok' : 'idle'}`}>
          {phones.length
            ? `${phones.length} ${phones.length === 1 ? 'telefon' : 'telefona'}`
            : 'nijedan'}
        </span>
      </header>
      <p className="muted">
        Rutine i Elena ti javljaju i kad je aplikacija zatvorena: pad servera, prijatelj ušao,
        podsetnik. U rutini dodaj korak „Notifikacija na telefon“.
      </p>
      {phones.length === 0 ? (
        <p className="notice subtle">
          Otvori Elena aplikaciju na telefonu → Više → Notifikacije i uključi „Javi mi na telefon“.
        </p>
      ) : (
        <ul className="list">
          {phones.map((p) => (
            <li key={p.deviceId} className="list-row">
              <Icon name="phone" />
              <span className="grow">
                {p.name}
                <span className="muted small block">
                  {p.platform === 'ios' ? 'iPhone' : 'Android'} · od{' '}
                  {new Date(p.addedAt).toLocaleDateString('sr-Latn-RS')}
                </span>
              </span>
              <button
                type="button"
                className="icon-btn ghost"
                aria-label={`Ukloni ${p.name}`}
                onClick={async () => {
                  await api.removePhone(p.deviceId).catch(() => undefined);
                  await reload();
                }}
              >
                <Icon name="trash" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="row gap-8 wrap">
        <button
          type="button"
          className="btn"
          disabled={busy || phones.length === 0}
          onClick={async () => {
            setBusy(true);
            try {
              setNote((await api.testPush()).message);
            } catch (err) {
              setNote(errorText(err, 'Nije poslato.'));
            } finally {
              setBusy(false);
            }
          }}
        >
          Pošalji probnu
        </button>
        {note && (
          <span className="muted small" role="status">
            {note}
          </span>
        )}
      </div>
    </section>
  );
}

function DiscordCard({
  data,
  reload,
  onNotice,
}: CardProps & { onNotice: (n: { ok: boolean; text: string }) => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const hooks = data.discord.webhooks;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await api.addWebhook(name.trim(), url.trim());
      setName('');
      setUrl('');
      await reload();
    } catch (err) {
      setFormError(errorText(err, 'Webhook nije sačuvan.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card integration" aria-labelledby="int-discord">
      <header className="card-head">
        <span className="integration-icon">
          <Icon name="hash" />
        </span>
        <h2 id="int-discord" className="grow">
          Discord
        </h2>
        <span className={`pill ${hooks.length ? 'ok' : 'idle'}`}>
          {hooks.length
            ? `${hooks.length} ${hooks.length === 1 ? 'kanal' : 'kanala'}`
            : 'nije povezan'}
        </span>
      </header>
      <p className="muted">
        „Javi u staff da restartujem server za 5 minuta.“ Elena piše preko webhooka, bez bota i bez
        pingova (@everyone ne radi).
      </p>

      {hooks.length > 0 && (
        <ul className="list">
          {hooks.map((h) => (
            <li key={h.name} className="list-row">
              <Icon name="hash" />
              <span className="grow">
                {h.name}
                <span className="muted small block mono">{h.url}</span>
              </span>
              <button
                type="button"
                className="btn"
                onClick={async () => {
                  try {
                    onNotice({ ok: true, text: (await api.testWebhook(h.name)).message });
                  } catch (err) {
                    onNotice({ ok: false, text: errorText(err, 'Discord nije primio poruku.') });
                  }
                }}
              >
                Test
              </button>
              <button
                type="button"
                className="icon-btn ghost"
                aria-label={`Ukloni kanal ${h.name}`}
                onClick={async () => {
                  await api.removeWebhook(h.name).catch(() => undefined);
                  await reload();
                }}
              >
                <Icon name="trash" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form className="stack-12" onSubmit={add}>
        <div className="row gap-8 wrap">
          <label className="sr-only" htmlFor="hook-name">
            Ime kanala
          </label>
          <input
            id="hook-name"
            className="field small-field"
            placeholder="Ime, npr. staff"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            required
          />
          <label className="sr-only" htmlFor="hook-url">
            Webhook adresa
          </label>
          <input
            id="hook-url"
            className="field grow-field"
            placeholder="https://discord.com/api/webhooks/…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            required
          />
          <button type="submit" className="btn primary" disabled={busy}>
            Dodaj
          </button>
        </div>
        {formError && <p className="notice">{formError}</p>}
        <details className="help">
          <summary>Gde nađem webhook?</summary>
          <ol>
            <li>Na Discordu: podešavanja kanala → Integrations → Webhooks → New Webhook.</li>
            <li>„Copy Webhook URL“ i nalepi ovde. Ime je kako ćeš kanal zvati Eleni.</li>
          </ol>
        </details>
      </form>
    </section>
  );
}

function SpotifyCard({ data, reload }: CardProps) {
  const s = data.spotify;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [now, setNow] = useState<NowPlaying | null>(null);

  useEffect(() => {
    if (!s.connected) return;
    let alive = true;
    const tick = () =>
      api
        .spotifyNow()
        .then((r) => alive && setNow(r.now))
        .catch(() => undefined);
    void tick();
    const t = window.setInterval(tick, 15_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [s.connected]);

  return (
    <section className="card integration" aria-labelledby="int-spotify">
      <header className="card-head">
        <span className="integration-icon">
          <Icon name="music" />
        </span>
        <h2 id="int-spotify" className="grow">
          Spotify
        </h2>
        <span className={`pill ${s.connected ? 'ok' : 'idle'}`}>
          {s.connected ? (s.user ?? 'povezan') : s.configured ? 'nije povezan' : 'nije podešen'}
        </span>
      </header>
      <p className="muted">
        „Pusti Cecu na Spotify-u“, „sledeća“, „pojačaj“. Ako Spotify nije otvoren, Elena ga sama
        upali na računaru. Kontrola traži Spotify Premium.
      </p>

      {s.connected && (
        <div className="now-playing" aria-live="polite">
          <span className={`dot ${now?.playing ? 'on' : 'idle'}`} />
          {now ? (
            <span className="grow">
              <span className="strong">{now.title}</span>
              <span className="muted small block">
                {now.artist}
                {now.device ? ` · ${now.device}` : ''}
              </span>
            </span>
          ) : (
            <span className="muted grow">Ništa ne svira.</span>
          )}
        </div>
      )}

      {!s.configured && (
        <ol className="setup-steps">
          <li>
            Na{' '}
            <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noreferrer">
              developer.spotify.com
            </a>{' '}
            napravi app (Web API).
          </li>
          <li>
            Redirect URI:{' '}
            {s.redirectUri ? (
              <code className="mono">{s.redirectUri}</code>
            ) : (
              <span>prvo podesi domen (ELENA_DOMAIN) da Elena ima HTTPS adresu</span>
            )}
          </li>
          <li>
            Client ID i Client secret upiši u <code>deploy/.env</code> kao{' '}
            <code>SPOTIFY_CLIENT_ID</code> i <code>SPOTIFY_CLIENT_SECRET</code>, pa ponovo pokreni{' '}
            <code>install.sh</code>.
          </li>
        </ol>
      )}

      {err && <p className="notice">{err}</p>}

      <div className="row gap-8 wrap">
        {s.configured && !s.connected && (
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                location.href = (await api.spotifyConnect()).url;
              } catch (e) {
                setErr(errorText(e, 'Ne mogu da otvorim Spotify prijavu.'));
                setBusy(false);
              }
            }}
          >
            Poveži Spotify
          </button>
        )}
        {s.connected && (
          <button
            type="button"
            className="btn danger"
            onClick={async () => {
              await api.spotifyDisconnect().catch(() => undefined);
              setNow(null);
              await reload();
            }}
          >
            Prekini vezu
          </button>
        )}
      </div>
    </section>
  );
}

function BuiltInCard({ data }: { data: IntegrationsResponse }) {
  const { devices } = useElena();
  const pc = devices.find((d) => d.kind === 'agent');
  const rows: Array<{ label: string; on: boolean; detail: string }> = [
    {
      label: 'Windows računar',
      on: Boolean(pc),
      detail: pc ? `${pc.name} · ${pc.capabilities.length} akcija` : 'Pokreni Elena agenta na PC-u',
    },
    {
      label: `FiveM · ${data.fivem.name}`,
      on: true,
      detail: `Povezivanje na ${data.fivem.address}`,
    },
    {
      label: 'YouTube',
      on: true,
      detail: data.youtube.exactMatch
        ? 'Pušta tačnu pesmu (YouTube Data API)'
        : 'Otvara pretragu; YOUTUBE_API_KEY pušta tačnu pesmu',
    },
    {
      label: 'Nežan glas',
      on: data.voice.enabled,
      detail: data.voice.enabled
        ? data.voice.name
        : 'Glas browsera/telefona; AZURE_SPEECH_KEY daje Sophie',
    },
  ];

  return (
    <section className="card integration" aria-labelledby="int-builtin">
      <header className="card-head">
        <span className="integration-icon">
          <Icon name="plug" />
        </span>
        <h2 id="int-builtin" className="grow">
          Ugrađeno
        </h2>
      </header>
      <ul className="list">
        {rows.map((r) => (
          <li key={r.label} className="list-row">
            <span className={`dot ${r.on ? 'on' : 'warn'}`} />
            <span className="grow">
              {r.label}
              <span className="muted small block">{r.detail}</span>
            </span>
          </li>
        ))}
      </ul>
      {!data.publicUrl && (
        <p className="muted small">
          Nema javne adrese (ELENA_DOMAIN). Bez nje nema HTTPS-a ni Spotify prijave.
        </p>
      )}
    </section>
  );
}

function BridgeCard({ data }: { data: IntegrationsResponse }) {
  const [state, setState] = useState<BridgeState | null>(null);
  const [checking, setChecking] = useState(false);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      setState(await api.bridge());
    } catch {
      setState({ configured: data.bridge.configured, status: null, error: 'Server ne odgovara.' });
    } finally {
      setChecking(false);
    }
  }, [data.bridge.configured]);

  useEffect(() => {
    void check();
  }, [check]);

  const live = Boolean(state?.status);
  return (
    <section className="card integration" aria-labelledby="int-bridge">
      <header className="card-head">
        <span className="integration-icon">
          <Icon name="terminal" />
        </span>
        <h2 id="int-bridge" className="grow">
          MILPET server (elena_bridge)
        </h2>
        <span className={`pill ${live ? 'ok' : state?.configured ? 'bad' : 'idle'}`}>
          {live ? 'povezan' : state?.configured ? 'ne odgovara' : 'nije podešen'}
        </span>
      </header>
      <p className="muted">
        „Restartuj imanje skriptu“, „javi igračima restart za 5 minuta“, „šta je puklo na serveru?“.
        Elena upravlja serverom preko malog resursa koji ti ubaciš.
      </p>
      {live && state?.status && (
        <dl className="facts">
          <div>
            <dt>Resursa radi</dt>
            <dd>{state.status.resources}</dd>
          </div>
          <div>
            <dt>Igrača</dt>
            <dd>{state.status.players}</dd>
          </div>
          <div>
            <dt>Grešaka u baferu</dt>
            <dd>{state.status.errors}</dd>
          </div>
        </dl>
      )}
      {state?.error && <p className="notice">{state.error}</p>}
      {!live && (
        <ol className="setup-steps">
          <li>
            Folder <code>fivem/elena_bridge</code> iz repozitorijuma prevuci u{' '}
            <code>resources</code> na serveru.
          </li>
          <li>
            U <code>server.cfg</code>: <code>set elena_bridge_token &quot;…&quot;</code>,{' '}
            <code>add_ace resource.elena_bridge command.ensure allow</code>,{' '}
            <code>ensure elena_bridge</code>. Token je <code>ELENA_BRIDGE_TOKEN</code> iz{' '}
            <code>deploy/.env</code>.
          </li>
          <li>Restartuj server, pa „Proveri“.</li>
        </ol>
      )}
      <div className="row gap-8 wrap">
        <button type="button" className="btn" onClick={() => void check()} disabled={checking}>
          Proveri
        </button>
        <span className="muted small mono break">{data.bridge.url}</span>
      </div>
    </section>
  );
}
